import { useEffect } from "react";
import { SITE } from "../lib/seo";

const upsertMeta = (attr, key, content) => {
  if (content == null) return;
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
};

export default function Seo({ title, description, path, image, type = "website", jsonLd, lang = "ru", noIndex = false }) {
  useEffect(() => {
    const pagePath = path || window.location.pathname;
    const clean = `${SITE.origin}${pagePath}`;
    const localized = lang === "en" ? `${clean}?lang=en` : clean;
    const img = image ? (image.startsWith("http") ? image : SITE.origin + image) : SITE.origin + SITE.defaultImage;
    const desc = description || (lang === "en" ? SITE.defaultDescriptionEn : SITE.defaultDescription);
    const hasBrand = /АРГАЛИ|ARGALI/i.test(title || "");
    const fullTitle = title ? (hasBrand ? title : `${title} — ${SITE.name}`) : SITE.defaultTitle;

    document.title = fullTitle;
    document.documentElement.setAttribute("lang", lang);

    upsertMeta("name", "description", desc);
    upsertMeta("name", "robots", noIndex ? "noindex, nofollow" : "index, follow");

    // Canonical: the Russian/default version uses the clean path; the English
    // translation has its own crawlable URL until we migrate to /en/ paths.
    let canon = document.head.querySelector('link[rel="canonical"]');
    if (!canon) {
      canon = document.createElement("link");
      canon.setAttribute("rel", "canonical");
      document.head.appendChild(canon);
    }
    canon.setAttribute("href", localized);

    // Open Graph
    upsertMeta("property", "og:title", fullTitle);
    upsertMeta("property", "og:description", desc);
    upsertMeta("property", "og:url", localized);
    upsertMeta("property", "og:type", type);
    upsertMeta("property", "og:image", img);
    upsertMeta("property", "og:image:alt", lang === "en" ? "АРГАЛИ (ARGALI) — Altai excursions" : "АРГАЛИ — экскурсии по Горному Алтаю");
    upsertMeta("property", "og:site_name", SITE.name);
    upsertMeta("property", "og:locale", lang === "en" ? "en_US" : "ru_RU");
    upsertMeta("property", "og:locale:alternate", lang === "en" ? "ru_RU" : "en_US");

    // Twitter
    upsertMeta("name", "twitter:card", "summary_large_image");
    upsertMeta("name", "twitter:title", fullTitle);
    upsertMeta("name", "twitter:description", desc);
    upsertMeta("name", "twitter:image", img);
    upsertMeta("name", "twitter:image:alt", lang === "en" ? "АРГАЛИ (ARGALI) — Altai excursions" : "АРГАЛИ — экскурсии по Горному Алтаю");

    // Localized alternatives. Both versions list themselves and each other.
    document.head.querySelectorAll("link[data-seo-alt]").forEach((n) => n.remove());
    const addAlt = (hreflang, href) => {
      const l = document.createElement("link");
      l.setAttribute("rel", "alternate");
      l.setAttribute("hreflang", hreflang);
      l.setAttribute("href", href);
      l.setAttribute("data-seo-alt", "1");
      document.head.appendChild(l);
    };
    addAlt("ru", clean);
    addAlt("en", `${clean}?lang=en`);
    addAlt("x-default", clean);

    // JSON-LD structured data
    document.head.querySelectorAll("script[data-seo-jsonld]").forEach((n) => n.remove());
    if (jsonLd) {
      const arr = Array.isArray(jsonLd) ? jsonLd : [jsonLd];
      arr.filter(Boolean).forEach((obj) => {
        const s = document.createElement("script");
        s.type = "application/ld+json";
        s.setAttribute("data-seo-jsonld", "1");
        s.text = JSON.stringify(obj);
        document.head.appendChild(s);
      });
    }
  }, [title, description, path, image, type, jsonLd, lang, noIndex]);

  return null;
}
